// Display names shared by the search index, the picker, the side cards and
// saved rolls.
import {words} from '../search/text.js';
import {sharedLoadoutName} from './shortNames.js';

// "Hexas, Strategic Security Division" -> "Hexas": what players call it.
/** @param {string | null | undefined} isc */
export const shortIsc = (isc) => (isc ?? '').split(',')[0].trim();

// The trooper's name for narrow columns (the ledger's source), in any
// language: the chosen loadout's name, which says who is rolling, even inside
// one unit (Überfallkommando: the Chimera or a Pupnik). Role tags (FTO, Lt)
// are dropped: "Hac Tao", "Fennec", "Caballero Teutón", "Chandra-1".
/**
 * @param {import('../types.js').UnitRow | null | undefined} unit
 * @param {import('../types.js').OptionRow | null | undefined} option
 */
export const unitTag = (unit, option) => sharedLoadoutName([option?.name ?? '']) ?? shortIsc(unit?.isc);

// The loadout's own name when it says something the unit name doesn't
// ("BIPANDRA", "Hacker"), else null. Army names most loadouts after the unit
// in capitals ("FENNEC" for Fennec Fusiliers, "FUSILIER" for Fusiliers), which
// is just noise next to the unit name.
/**
 * @param {string | null | undefined} loadout
 * @param {string} unitName
 * @returns {string | null}
 */
export function extraLoadoutName(loadout, unitName) {
  if (!loadout) return null;
  const unitWords = words(unitName);
  const same = words(loadout).every((w) => unitWords.some((u) => u.startsWith(w) || w.startsWith(u)));
  return same ? null : loadout;
}

// Short weapon names for buttons, the way players say them ("HMG",
// "Missile L."), so a card's weapons fit on fewer rows. The full name stays
// in the button's title and in the results. Per language, for the names in
// that language's names file. Spanish follows the Army's own short forms
// ("F. Precisión" in its extras); its launchers are one word already
// ("Lanzamisiles").
/** @type {Record<string, [RegExp, string][]>} */
const SHORT_WEAPON = {
  en: [
    [/Heavy Machine Gun/, 'HMG'],
    [/Submachine Gun/, 'SMG'],
    [/Hyper-Rapid Magnetic Cannon/, 'HMC'],
    [/Portable Autocannon/, 'Autocannon'],
    [/(Missile|Grenade|Rocket) Launcher/, '$1 L.'],
    [/MULTI (Sniper|Marksman) Rifle/, 'MULTI $1'],
  ],
  es: [
    [/^Fusil de (Francotirador|Precisión)/, 'F. $1'],
    [/^Cañ[oó]n Magnético Hiperrápido/, 'CMH'],
    [/^Cañ[oó]n Portátil Automático/, 'Autocañón'],
  ],
};

/**
 * @param {string | null | undefined} name
 * @param {string} [lang]
 */
export function shortWeaponName(name, lang = 'en') {
  return (SHORT_WEAPON[lang] ?? SHORT_WEAPON.en).reduce((n, [re, to]) => n.replace(re, to), name ?? '');
}
